import { ref, computed } from 'vue';
import TelephonyAPI from 'dashboard/api/telephony';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  VOICE_CALL_PROVIDERS,
  getVoiceCallProvider,
} from 'dashboard/helper/inbox';

// Линии оператора в АТС: на каких телефонных инбоксах ОН может звонить.
//
// Звонок уходит от имени сотрудника АТС, поэтому оператору без привязки кнопка
// звонка только отвечала «У вас нет доступа к звонкам»: он её видел, жал и
// получал отказ. Здесь один запрос на аккаунт, а компоненты просто спрашивают,
// показывать ли кнопку для конкретного инбокса.
//
// У Twilio и WhatsApp привязки к сотруднику нет вовсе (звонит сам аккаунт), так
// что их кнопок этот список не касается.
const PBX_PROVIDERS = [
  VOICE_CALL_PROVIDERS.UIS,
  VOICE_CALL_PROVIDERS.MANGO,
  VOICE_CALL_PROVIDERS.MEGAPBX,
];

// Кэш по аккаунту: оператор может переключиться в другой аккаунт, и линии там
// свои. undefined — ещё не спрашивали, null — спросить не вышло.
const linesByAccount = ref({});
const inFlight = {};

const fetchLines = accountId => {
  if (!accountId) return Promise.resolve(null);
  if (linesByAccount.value[accountId] !== undefined) {
    return Promise.resolve(linesByAccount.value[accountId]);
  }
  if (!inFlight[accountId]) {
    inFlight[accountId] = TelephonyAPI.myLines()
      .then(({ data }) => {
        linesByAccount.value = {
          ...linesByAccount.value,
          [accountId]: data?.lines || [],
        };
        return linesByAccount.value[accountId];
      })
      // Не смогли узнать — кнопку не прячем: пусть лучше покажет ошибку, чем
      // исчезнет без объяснений.
      .catch(() => {
        linesByAccount.value = { ...linesByAccount.value, [accountId]: null };
        return null;
      })
      .finally(() => {
        delete inFlight[accountId];
      });
  }
  return inFlight[accountId];
};

export const useVoiceLines = () => {
  const { accountId } = useAccount();

  fetchLines(accountId.value);

  const lines = computed(() => linesByAccount.value[accountId.value]);

  // Можно ли звонить из этого инбокса. Пока список не приехал — да, иначе
  // кнопка мигала бы при каждом открытии карточки.
  const canDialFrom = inbox => {
    const provider = getVoiceCallProvider(inbox);
    if (provider === null) return false;
    if (!PBX_PROVIDERS.includes(provider)) return true;
    if (!Array.isArray(lines.value)) return true;

    const inboxId = inbox.id ?? inbox.inbox_id;
    return lines.value.some(line => line.inbox_id === inboxId);
  };

  return { canDialFrom, refreshVoiceLines: () => fetchLines(accountId.value) };
};
